import {useLayoutEffect} from 'react'
import {navigate, navigateModal, useLocation} from '@eitje/bridge'

// 10 seconds for the metadata to be considered fresh
const METADATA_TTL = 10000
const METADATA_KEY = 'eitje-modal-route-metadata'

// When a new tab is opened with a modal route (e.g. via Direct URL, cmd+click, etc..),
// local storage is used to carry over the existing modal background route
// so the new tab can navigate back smoothly to the intended background.
export const setModalRouteMetadataInLocalStorage = ({to, backgroundPath}) => {
	localStorage.setItem(
		METADATA_KEY,
		JSON.stringify({
			pathname: to,
			backgroundPath,
			createdAt: Date.now(),
		}),
	)
}

export const useHydrateModalRouteFromLocalStorage = () => {
	const location = useLocation()
	const metadata = readMetadata()
	const hasValidMetadata = isMetadataValid(metadata)
	const backgroundPath = metadata?.backgroundPath
	const isHydrating = !location.state?.background && hasValidMetadata

	useLayoutEffect(() => {
		if (!isHydrating) return

		const modalPath = `${location.pathname}${location.search}`

		const hydrateModalRoute = async () => {
			clearMetadata()

			// 1. Manually reset first entry in the history stack
			await navigate(backgroundPath, {replace: true})
			// 2. Manually navigate to the target modal
			await navigateModal(modalPath)
		}

		hydrateModalRoute()
	}, [backgroundPath, isHydrating, location.pathname, location.search])

	return {isHydrating}
}

const readMetadata = () => {
	const storedMetadata = localStorage.getItem(METADATA_KEY)

	if (!storedMetadata) return null

	try {
		return JSON.parse(storedMetadata)
	} catch {
		return null
	}
}

// Metadata can be valid but not necessarily meant for the current caller, but since we are clearing the metadata upon consumption, this risk is negligeble.
// A TTL check is needed to prevent stale metadata from consumption by an unwanted modal.
const isMetadataValid = metadata =>
	Boolean(metadata?.pathname && metadata?.backgroundPath && metadata.createdAt > Date.now() - METADATA_TTL)

const clearMetadata = () => {
	localStorage.removeItem(METADATA_KEY)
}
